import api, { ApiResponse } from './api';

export interface SyncJob {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'partial' | 'cancelled';
  progress: number;
  recordsSynced: number;
  error?: string;
  errors?: Array<{
    type: string;
    message: string;
    entityId?: string;
  }>;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface SyncStatus {
  lastSync: {
    id: string;
    status: string;
    completedAt: string;
    recordsSynced: number;
    error?: string;
  } | null;
  syncInProgress: {
    id: string;
    progress: number;
    startedAt: string;
  } | null;
}

export interface SyncEntityResult {
  jobId: string;
  status: 'queued' | 'completed' | 'failed';
  action: 'publish' | 'sync';
  recordsSynced?: number;
  errors?: Array<{
    type: string;
    message: string;
  }>;
}

export type EntityType = 'campaign' | 'adset' | 'ad';

export const syncService = {


  /**
   * Get sync status for an ad account
   */
  async getStatus(adAccountId: string): Promise<SyncStatus> {
    const response = await api.get<ApiResponse<SyncStatus>>(
      `/sync/status/${adAccountId}`
    );
    return response.data.data!;
  },

  /**
   * List sync jobs
   */
  async listJobs(params?: {
    adAccountId?: string;
    status?: string;
    page?: number;
    perPage?: number;
  }): Promise<{ jobs: SyncJob[]; total: number }> {
    const response = await api.get<ApiResponse<SyncJob[]>>('/sync/jobs', { params });
    return {
      jobs: response.data.data || [],
      total: (response.data as unknown as { pagination?: { total: number } }).pagination?.total || 0,
    };
  },

  /**
   * Get a specific sync job
   */
  async getJob(jobId: string): Promise<SyncJob> {
    const response = await api.get<ApiResponse<SyncJob>>(`/sync/jobs/${jobId}`);
    return response.data.data!;
  },

  /**
   * Cancel a pending/running sync job
   */
  async cancelJob(jobId: string): Promise<boolean> {
    const response = await api.post<ApiResponse<{ cancelled: boolean }>>(
      `/sync/jobs/${jobId}/cancel`
    );
    return response.data.data!.cancelled;
  },

  /**
   * Sync insights from Facebook for an ad account
   */
  async syncInsights(
    adAccountId: string,
    dateRange?: { date_from: string; date_to: string }
  ): Promise<{ jobId: string; status: string; recordsSynced?: number }> {
    const response = await api.post<
      ApiResponse<{ jobId: string; status: string; recordsSynced?: number }>
    >(`/sync/insights/${adAccountId}`, dateRange || {});
    return response.data.data!;
  },

  /**
   * Pull entity state from Facebook (effective_status updates)
   */
  async pullState(
    adAccountId: string
  ): Promise<{ jobId: string; status: string; recordsSynced?: number }> {
    const response = await api.post<
      ApiResponse<{ jobId: string; status: string; recordsSynced?: number }>
    >(`/sync/pull/${adAccountId}`);
    return response.data.data!;
  },
};
